import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, MenuItem,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, InputAdornment, IconButton, Tooltip, Avatar,
  Dialog, DialogTitle, DialogContent, DialogActions, Grid,
} from '@mui/material';
import { Refresh, Search, Add, Phone, CheckCircle, Delete, Restore } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';
import dayjs from 'dayjs';

const STATUS_TABS = [
  { key: 'Active',    label: 'Active',    bg: '#e8f5e9', color: '#2e7d32' },
  { key: 'Completed', label: 'Completed', bg: '#e8eaf6', color: '#1a237e' },
  { key: 'Inactive',  label: 'Deleted',   bg: '#ffebee', color: '#c62828' },
];

export default function Leads() {
  const [activeStatus, setActiveStatus] = useState('Active');
  const [records, setRecords] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [actionLoading, setActionLoading] = useState({});
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState({ name: '', phone_number: '', reason: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const data = await InvokeApi(Config.main_url + apiList.getVisitors, { status: activeStatus });
    const list = data?.visitors || [];
    setRecords(list);
    setFiltered(list);
    setLoading(false);
  }, [activeStatus]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(records.filter(r =>
      r.name?.toLowerCase().includes(q) ||
      String(r.phone_number || '').includes(q) ||
      r.reason?.toLowerCase().includes(q)
    ));
  }, [search, records]);

  const updateStatus = async (id, status) => {
    setActionLoading(p => ({ ...p, [id]: status }));
    await InvokeApi(Config.main_url + apiList.deleteVisitors, { id, status });
    setActionLoading(p => ({ ...p, [id]: null }));
    load();
  };

  const handleAdd = async () => {
    if (!form.name || !form.phone_number) { alert('Name and phone are required'); return; }
    setSaving(true);
    const data = await InvokeApi(Config.main_url + apiList.addVisitor, {
      name: form.name,
      phone_number: form.phone_number,
      reason: form.reason || '',
    });
    setSaving(false);
    if (data?.code === '200') {
      setDialog(false);
      setForm({ name: '', phone_number: '', reason: '' });
      load();
    } else alert(data?.message || 'Failed');
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const activeTab = STATUS_TABS.find(t => t.key === activeStatus);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Leads / Enquiries</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} {activeStatus.toLowerCase()} leads
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{ border: '1px solid #e8eaf6', borderRadius: 2, color: '#1a237e' }}>
              <Refresh />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<Add />} onClick={() => setDialog(true)}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            Add Lead
          </Button>
        </Box>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField size="small" placeholder="Search name, phone, reason..." value={search}
            onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />

          <Box sx={{ display: 'flex', gap: 1 }}>
            {STATUS_TABS.map(t => (
              <Chip key={t.key} label={t.label} clickable onClick={() => setActiveStatus(t.key)}
                sx={{
                  fontWeight: 700, fontSize: 12,
                  background: activeStatus === t.key ? t.bg : '#f5f6fa',
                  color: activeStatus === t.key ? t.color : 'text.secondary',
                  border: activeStatus === t.key ? `1.5px solid ${t.color}` : '1.5px solid transparent',
                }}
              />
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'Name', 'Phone', 'Reason', 'Date', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No {activeStatus.toLowerCase()} leads found
                    </TableCell>
                  </TableRow>
                ) : filtered.map((r, i) => {
                  const tab = STATUS_TABS.find(t => t.key === r.status) || STATUS_TABS[0];
                  const isActing = actionLoading[r.id];
                  return (
                    <TableRow key={r.id || i} hover>
                      <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar src={r.image_path} sx={{
                            width: 34, height: 34,
                            background: 'linear-gradient(135deg,#1a237e,#1565c0)',
                            fontSize: 13, fontWeight: 700, color: '#fff',
                          }}>
                            {r.name?.[0]}
                          </Avatar>
                          <Typography fontWeight={600} fontSize={13}>{r.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.phone_number}</TableCell>
                      <TableCell sx={{ fontSize: 12, maxWidth: 200 }}>
                        <Typography fontSize={12} noWrap>{r.reason || '—'}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, color: 'text.secondary' }}>
                        {r.created_date ? dayjs(r.created_date).format('DD MMM YYYY') : '—'}
                      </TableCell>
                      <TableCell>
                        <Chip label={r.status || 'Active'} size="small"
                          sx={{ background: tab.bg, color: tab.color, fontWeight: 600, fontSize: 11 }} />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          {/* Call */}
                          <Tooltip title="Call">
                            <IconButton size="small" component="a" href={`tel:${r.phone_number}`}
                              sx={{ color: '#e65100', background: '#fff3e0', borderRadius: 1.5 }}>
                              <Phone sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>

                          {r.status === 'Active' && (
                            isActing === 'Completed' ? (
                              <CircularProgress size={18} sx={{ color: '#2e7d32', mx: 0.5 }} />
                            ) : (
                              <Tooltip title="Mark Completed">
                                <IconButton size="small" onClick={() => updateStatus(r.id, 'Completed')}
                                  sx={{ color: '#2e7d32', background: '#e8f5e9', borderRadius: 1.5 }}>
                                  <CheckCircle sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )
                          )}

                          {(r.status === 'Completed' || r.status === 'Inactive') && (
                            isActing === 'Active' ? (
                              <CircularProgress size={18} sx={{ color: '#1a237e', mx: 0.5 }} />
                            ) : (
                              <Tooltip title="Restore to Active">
                                <IconButton size="small" onClick={() => updateStatus(r.id, 'Active')}
                                  sx={{ color: '#1a237e', background: '#e8eaf6', borderRadius: 1.5 }}>
                                  <Restore sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )
                          )}

                          {r.status !== 'Inactive' && (
                            isActing === 'Inactive' ? (
                              <CircularProgress size={18} sx={{ color: '#c62828', mx: 0.5 }} />
                            ) : (
                              <Tooltip title="Delete">
                                <IconButton size="small" onClick={() => updateStatus(r.id, 'Inactive')}
                                  sx={{ color: '#c62828', background: '#ffebee', borderRadius: 1.5 }}>
                                  <Delete sx={{ fontSize: 16 }} />
                                </IconButton>
                              </Tooltip>
                            )
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          Add New Lead
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Name *" value={form.name} onChange={f('name')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Phone *" value={form.phone_number} onChange={f('phone_number')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Reason / Enquiry" value={form.reason} onChange={f('reason')} multiline rows={2} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : 'Add Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
